import * as React from 'react';
import * as Handlebars from 'handlebars';
import * as _ from 'lodash';
import {
  Logging,
  Notification,
  NotificationData,
  ContentLoading,
  renderTextArea,
  ControlState,
} from '@vcd-ecm-ui/Common';
import { VtxButton, VtxIcon, VtxModal } from '@vertexinc/vtx-ui-react-component-library';
import useTemplate from './useTemplate';
import TemplateProps from './TemplateProps.interface';
import '../templates.scss';
import { handleBarSampleDataVertexDomain, handleBarSampleDataCustomDomain } from '../../../../template/HandleBarSample';

export const DefaultDomainName = 'Vertexinc.com';

const controlState: ControlState = {
  editable: true,
  disabled: false,
  readOnly: false,
};

const bodyStyle = {
  backgroundColor: 'white',
};

const Template = ({
  gatewayEndPoint,
  template,
  configuration,
  isOpen,
  onClose,
  canEdit,
}: TemplateProps): JSX.Element => {
  const [notification, setNotification] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [templateContent, setTemplateContent] = React.useState(null);
  const [templateContentPreview, setTemplateContentPreview] = React.useState(null);
  const [edit, setEdit] = React.useState(canEdit);
  const [error, setError] = React.useState(null);

  // Load the Template
  const [templateHtml, templateNotification] = useTemplate(gatewayEndPoint, template.presignedUrl);

  const vertexDomain: boolean = configuration?.domain?.toLowerCase() === DefaultDomainName.toLowerCase();

  Logging.Info('Template', canEdit ? 'Edit' : 'View', vertexDomain, configuration);

  const generatePreview = (html: string): boolean => {
    try {
      const handleBarTemplate = Handlebars.compile(html);

      // Use the correct Sample Data based on the Domain
      setTemplateContentPreview(handleBarTemplate(vertexDomain ? handleBarSampleDataVertexDomain : handleBarSampleDataCustomDomain));
      setError(null);

      return true;
    } catch (handleBarError) {
      setTemplateContentPreview('Unable to process the Handlebar template: ');
      setError(handleBarError);
      Logging.Info(handleBarError);

      return false;
    }
  };

  // Notification Error was Closed
  const onCloseNotification = (): void => {
    setNotification(null);
  };

  const onCloseModal = (): void => {
    Logging.Info('Template Close');
    onClose();
  };

  const onSaveChanges = (): void => {
    Logging.Info('Template Save');
    onClose();
  };

  const onPreviewEdit = (): void => {
    Logging.Info('Template Preview / Edit');
    let result = true;

    // If in Edit Mode, generate a Preview from the latest template Content
    if (edit) {
      result = generatePreview(templateContent);
    }

    // Only toggle if there are no errors
    if (result) {
      setEdit(!edit);
    }
  };

  const canSave = (): boolean => {
    if (canEdit) {
      if (templateContent === templateHtml) {
        return false;
      }

      // Save enable only when in Preview Mode
      return !edit;
    }

    return false;
  };

  // Loading is Done - Successfull
  React.useEffect(() => {
    if (templateHtml) {
      setTemplateContent(templateHtml);

      // Preview Mode
      if (!canEdit) {
        generatePreview(templateHtml);
      }

      setIsLoading(false);
    }
  }, [templateHtml]);

  // Loading is Done - Error
  React.useEffect(() => {
    if (templateNotification) {
      setIsLoading(false);
      setNotification(templateNotification);
    }
  }, [templateNotification]);

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('TemplateView: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
      setIsLoading(false);
      setNotification(null);
      setError(null);
      setTemplateContent(null);
      setTemplateContentPreview(null);
    };
  }, []);

  const Spacer = () => <span style={{ marginRight: '10px' }} />;
  const saveEnabled: boolean = canSave();

  return (
    <>
      <VtxModal
        visible={isOpen}
        onCancel={(): void => onCloseModal()}
        width="50%"
        bodyStyle={bodyStyle}
        footer={null}
        closable={false}
      >
        <div className="templateContainer">
          {
            isLoading
              ? (
                <ContentLoading loadingMessage="Loading Template ..." />
              )
              : (
                <>
                  <div className="templateHeader">
                    <div className="templateHeaderLeft">
                      {template.templateName}
                    </div>
                    <div className="templateHeaderRight">
                      <VtxButton
                        id="sbsViewerCloseButton"
                        type="link"
                        onClick={(): void => onCloseModal()}
                        icon={<VtxIcon type="close-small" fontSize={12} />}
                      />
                    </div>
                  </div>
                  <div className="templateHeaderMessage">
                    Fill in the information below to create your custom template.
                  </div>
                  <div className="templateHorizontalLine" />
                  <div className="templateCorrespondence">
                    Correspondence Text
                  </div>
                  <div className="templateContentView">
                    {
                      edit
                        ? (
                          <>
                            {renderTextArea(
                              'additionalInfo',
                              null,
                              templateContent,
                              'Provide Template Content...',
                              20,
                              setTemplateContent,
                              controlState,
                              'templateContentTextArea',
                            )}
                          </>
                        )
                        : (
                          /* eslint-disable-next-line  react/no-danger */
                          <div className="templateContentHtml" dangerouslySetInnerHTML={{ __html: `${templateContentPreview || ''}` }} />
                        )
                    }
                  </div>
                  <div className="templateContentError">
                    {error ? error.message : null}
                  </div>
                  <div className="templateHorizontalLine" />
                  <div className="templateFooter">
                    {
                      canEdit
                        ? (
                          <>
                            <div className="templateFooterLeft">
                              <VtxButton
                                id="saveButton"
                                onClick={onSaveChanges}
                                type="primary"
                                disabled={!saveEnabled}
                              >
                                Save Changes
                              </VtxButton>
                              <Spacer />
                              <VtxButton
                                id="cancelButton"
                                onClick={onCloseModal}
                              >
                                Cancel
                              </VtxButton>
                            </div>
                            <div className="templateFooterRight">
                              <VtxButton
                                id="previewEditButton"
                                onClick={onPreviewEdit}
                                type="primary-deprecated"
                              >
                                {edit ? 'Preview Template' : 'Edit Template'}
                              </VtxButton>
                            </div>
                          </>
                        )
                        : (
                          <div className="templateFooterLeft">
                            <VtxButton
                              id="backButton"
                              onClick={onCloseModal}
                              type="primary"
                            >
                              Back To Templates
                            </VtxButton>
                          </div>
                        )
                    }
                  </div>
                </>
              )
          }
          <Notification
            data={notification}
            onNotificationClose={onCloseNotification}
            autoClose
          />
        </div>
      </VtxModal>
    </>
  );
};

export default Template;
